<template>
  <div class="general-wrapper">
    <Header>
      <template #title>
        <div class="gap-column-sm">
          <div class="qrw-heading-sm">
            {{ $t('navbar.billing') }}
          </div>
          <div class="gap-column-1xs">
            <BaseTabButton size="sm" :active="selectedTab == 'plan'" @click="selectedTab = 'plan'">
              {{ $t('superbilling.tab_plan') }}
            </BaseTabButton>
            <BaseTabButton size="sm" :active="selectedTab == 'details'" @click="selectedTab = 'details'">
              {{ $t('superbilling.tab_details') }}
            </BaseTabButton>
          </div>
        </div>

      </template>
    </Header>

    <PageContainer contentClass="superbilling-global-container" v-if="selectedTab == 'plan'">
      <div class="status-container gap-row-xs">
        <div class="validity-block">
          <div class="validity-column">
            <div class="qrw-label-sm qrw-text-content-base-tertiary">
              {{ $t('superbilling.valid_till') }}
            </div>
            <div class="qrw-heading-sm bold qrw-text-content-base-primary">
              {{ validTillDate }}
            </div>
            <div class="qrw-label-sm qrw-text-content-warning-primary" v-if="freePeriodDaysLeft">
              {{ $t('superbilling.free_period') }}: {{ freePeriodDaysLeft }} {{ $t('units.days') }}
            </div>
          </div>
          <template v-if="isBillingActive">
            <div class="billing-divider"></div>
            <div class="validity-column">
              <div class="qrw-label-sm qrw-text-content-base-tertiary">
                {{ $t('superbilling.pricing_plan') }}
              </div>
              <div class="qrw-heading-sm bold qrw-text-content-base-primary">
                {{ pricingPlanName }}
              </div>
              <div class="qrw-label-sm qrw-text-content-base-tertiary gap-column-zero">
                <template v-for="(srv, index) in cafe.billing.selected_services">
                  {{ capitalize(srv) }}
                  <BaseIcon icon="dot" size="xs" v-if="index != cafe.billing.selected_services.length - 1"
                    :key="index" />
                </template>
              </div>
            </div>
            <div class="billing-divider"></div>
            <div class="validity-column">
              <div class="qrw-label-sm qrw-text-content-base-tertiary">
                {{ $t('superbilling.payment_sum') }}
              </div>
              <div class="qrw-heading-sm bold qrw-text-content-base-primary">
                $ {{ cafe.billing.total_sum }}
              </div>
              <div class="qrw-label-sm qrw-text-content-base-tertiary">
                {{ cafe.billing.periodicity === 'monthly' ? $t('superbilling.every_month') :
              $t('superbilling.every_year') }}
              </div>
            </div>
          </template>
          <template v-else>
            <div class="billing-divider"></div>
            <div class="no-plan-column">
              <div class="gap-row-1xs">
                <div class="qrw-label-lg bold qrw-text-content-base-primary">
                  {{ $t('superbilling.pricing_plan') }}
                </div>
                <div class="qrw-paragraph-sm qrw-text-content-base-secondary">
                  {{ $t('superbilling.empty_hint') }}
                </div>
              </div>

              <BasePushButton @click="selectingPlan = true" size="sm" variant="fill"
                bgColor="background-base-inverse-primary" textColor="content-base-inverse-primary">
                {{ $t('superbilling.empty_select') }}
              </BasePushButton>
            </div>
          </template>
        </div>
        <div class="change-plan" v-if="isBillingActive">
          <div class="qrw-paragraph-sm qrw-text-content-base-tertiary">
            {{ $t('superbilling.pricing_plan') }}: {{ pricingPlanName }} ({{ cafe.billing.selected_services.length }}
            {{ $tc('superbilling.services_noun', cafe.billing.selected_services.length) }}), {{ cafe.billing.periodicity
              ===
              'monthly' ? $t('superbilling.every_month') : $t('superbilling.every_year') }}
            ({{ calendarDayNumber }}{{ $t('superbilling.calendar_day') }}), $ {{ cafe.billing.total_sum }}
          </div>
          <BasePushButton variant="primary" size="sm" bgColor="background-warning-default"
            textColor="content-base-inverse-primary" @click="selectingPlan = true">
            {{ $t('superbilling.change_plan') }}
          </BasePushButton>
        </div>
      </div>
      <div class="gap-row-xs" v-if="cafe.billing.auto_payment_enabled">
        <div class="qrw-heading-xs bold qrw-text-content-base-primary">
          {{ $t('superbilling.auto_payment') }}
        </div>
        <div class="auto-payment-container">
          <div class="auto-payment">
            <div class="auto-payment-status gap-column-xs">
              <div class="gap-row-1xs w-100">
                <div class="qrw-label-sm qrw-text-content-base-tertiary">
                  {{ $t('superbilling.ap_status') }}
                </div>
                <div class="qrw-label-lg bold qrw-text-content-success-primary">
                  {{ $t('superbilling.ap_enabled') }}
                </div>
              </div>
              <BasePushButton size="sm" variant="fill" bgColor="background-error-secondary"
                textColor="content-error-primary" @click="showDisableAutopaymentModal">
                {{ $t('superbilling.ap_disable') }}
              </BasePushButton>
            </div>
            <div class="billing-divider"></div>
            <div class="auto-payment-card">
              <div class="gap-row-1xs w-100">
                <div class="qrw-label-sm qrw-text-content-base-secondary">
                  {{ $t('superbilling.ap_card') }}
                </div>
                <div class="qrw-label-lg qrw-text-content-base-primary">
                  {{ cafe.billing.auto_payment_card }}
                </div>
              </div>

              <form :action="`/api/superbilling/change-card/${cafe._id}`" method="POST">
                <BasePushButton size="sm" type="submit" variant="fill" bgColor="background-base-tertiary"
                  textColor="content-base-secondary">
                  {{ $t('superbilling.ap_change') }}
                </BasePushButton>
              </form>

            </div>
            <div class="billing-divider"></div>
            <div class="auto-payment-attempts">
              <div class="gap-row-1xs w-100">
                <div class="qrw-label-sm qrw-text-content-base-secondary">
                  {{ $t('superbilling.next_payment') }}
                </div>
                <div class="qrw-label-lg qrw-text-content-warning-primary bold">
                  {{ nextPaymentDate | formatDate }}
                </div>
              </div>
              <BasePulldownButton size="sm" bgColor="background-base-tertiary" textColor="content-base-secondary"
                variant="primary" @click="extendedAutoPayment = !extendedAutoPayment">
                {{ extendedAutoPayment ? $t('superbilling.ap_less') : $t('superbilling.ap_more') }}
              </BasePulldownButton>
            </div>
          </div>
          <div class="auto-payment-details" v-if="extendedAutoPayment">
            <div class="auto-payment-attempts">
              <template v-for="n in 4">
                <div class="gap-row-1xs w-100" :key="n">
                  <div class="qrw-label-sm" :class="{
              'qrw-text-content-base-tertiary': true,
              'qrw-text-content-warning-primary': n - 1 === cafe.billing.auto_payment_attempts,
              'qrw-text-content-error-primary': (n - 1) < cafe.billing.auto_payment_attempts
            }">
                    {{ n }}{{ $t('superbilling.ap_attempt') }}
                  </div>
                  <div class="qrw-heading-xs bold" :class="{
              'qrw-text-content-base-tertiary': true,
              'qrw-text-content-warning-primary': n - 1 === cafe.billing.auto_payment_attempts,
              'qrw-text-content-error-primary': (n - 1) < cafe.billing.auto_payment_attempts
            }">
                    {{ getPaymentAttemptDate(n - 1) }}
                  </div>
                </div>
                <BaseIcon icon="arrow-block-right" size="md" :class="{
              'qrw-text-content-base-tertiary': true,
              'qrw-text-content-warning-primary': n - 1 === cafe.billing.auto_payment_attempts,
              'qrw-text-content-error-primary': (n - 1) < cafe.billing.auto_payment_attempts
            }" :key="`divider_${n}`" v-if="n != 4" />
              </template>

            </div>
            <BaseDivider color="border-base-tertiary" />
            <div class="qrw-paragraph-sm qrw-text-content-base-tertiary auto-payment-hint">
              {{ $t('superbilling.ap_warning') }}
            </div>
          </div>
        </div>
      </div>
      <div class="gap-row-xs">
        <div class="qrw-heading-xs bold qrw-text-content-base-primary">
          {{ $t('superbilling.invoices') }}
        </div>
        <div class="billing-table">
          <div class="billing-table-header">
            <div class="header-column bt-col-short">#</div>
            <div class="header-column bt-col-medium">{{ $t('superbilling.invoice_id') }}</div>
            <div class="header-column bt-col-medium">{{ $t('superbilling.due_date') }}</div>
            <div class="header-column bt-col-services">{{ $t('superbilling.services') }}</div>
            <div class="header-column bt-col-sum">{{ $t('superbilling.sum') }}</div>
            <div class="header-column bt-col-method">{{ $t('superbilling.payment_method') }}</div>
            <div class="header-column bt-col-status">{{ $t('superbilling.status') }}</div>
            <div class="header-column bt-col-actions"></div>
          </div>
          <div class="billing-table-rows">
            <div class="billing-table-item" v-for="(invoice, index) in invoices" :key="invoice._id">
              <div class="table-item-col bt-col-short qrw-label-md qrw-text-content-base-primary">
                {{ invoices.length - index }}
              </div>
              <div class="table-item-col bt-col-medium qrw-label-md qrw-text-content-base-primary">
                {{ invoice.invoice_number }}
              </div>
              <div class="table-item-col bt-col-medium qrw-label-md qrw-text-content-base-primary">
                {{ invoice.due_date | formatDate }}
              </div>
              <div class="table-item-col table-item-col-plan bt-col-services">
                <div class="qrw-label-md qrw-text-content-base-secondary">
                  {{ capitalize(invoice.plan || 'Dynamic') }}
                </div>
                <div class="gap-column-zero">
                  <template v-for="(srv, si) in invoice.services">
                    <div class="qrw-label-sm qrw-text-content-base-tertiary" :key="si">
                      {{ formatServiceName(srv.name) }}
                    </div>
                    <BaseIcon size="xs" icon="dot" class="qrw-text-content-base-tertiary" :key="`ic${si}`"
                      v-if="si != invoice.services.length - 1" />
                  </template>
                </div>

              </div>
              <div class="table-item-col qrw-label-md qrw-text-content-base-primary bt-col-sum"
                v-if="invoice.total_uah && showUah">
                {{ invoice.total_uah }} ₴
              </div>
              <div class="table-item-col qrw-label-md qrw-text-content-base-primary bt-col-sum" v-else>
                $ {{ invoice.total }}
              </div>
              <div class="table-item-col table-item-col-plan bt-col-method">
                <div class="qrw-label-md qrw-text-content-base-primary" v-if="invoice.payment_method_type">
                  {{ invoice.payment_method_type === 'card' ? $t('superbilling.card') :
              $t('superbilling.bank_transfer') }}
                </div>
                <div class="qrw-label-sm qrw-text-content-base-tertiary" v-if="invoice.payment_method_info">
                  {{ invoice.payment_method_info }}
                </div>
              </div>
              <div class="table-item-col bt-col-status qrw-text-content-error-primary qrw-label-md"
                v-if="invoice.status === 'unpaid'">
                {{ $t('superbilling.unpaid') }}
              </div>
              <div class="table-item-col bt-col-status qrw-text-content-success-primary qrw-label-md"
                v-else-if="invoice.status === 'paid'">
                {{ $t('superbilling.paid') }}
              </div>
              <div class="table-item-col bt-col-status qrw-text-content-base-tertiary qrw-label-md"
                v-else-if="invoice.status === 'refunded'">
                {{ $t('superbilling.refunded') }}
              </div>
              <div class="table-item-col bt-col-status qrw-text-content-warning-primary qrw-label-md"
                v-else-if="invoice.status === 'pending'">
                Pending
              </div>
              <div class="table-item-col bt-col-actions">
                <BasePushButton variant="primary" size="sm" textColor="content-base-inverse-primary"
                  bgColor="background-success-default"
                  v-if="invoice.status === 'unpaid' && !cafe.billing.auto_payment_enabled"
                  @click="payingInvoice = invoice">
                  {{ $t('superbilling.pay_invoice') }}
                </BasePushButton>
                <a :href="`/api/superbilling/invoices/download/${invoice._id}`" target="_blank"
                  style="display: content;">
                  <BaseIconButton icon="download-cloud" size="sm" variant="primary" textColor="content-base-tertiary"
                    bgColor="background-base-tertiary" />

                </a>

              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>

    <PageContainer contentClass="superbilling-global-container" v-else>
      <div class="iqos-grid">
        <div class="iqos-column">
          <BillingCompanyDetailsPanel />
        </div>
        <div class="iqos-column">
          <BillingEmailsPanel />
        </div>
      </div>
    </PageContainer>

    <PayInvoiceModal @close="payingInvoice = null" :invoice="payingInvoice" v-if="payingInvoice" />

    <SelectPlanModal @close="selectingPlan = false" @confirm="onPlanChange" v-if="selectingPlan" />

    <DangerConfirmModal @confirm="disableAutopayment" @cancel="confirmingAutoPaymentDisable = null"
      v-if="confirmingAutoPaymentDisable">{{ $t("superbilling.confirm_disable") }}</DangerConfirmModal>
  </div>
</template>

<script>
import SuperbillingAPI from '@/api/superbilling'

import PayInvoiceModal from '@/components/superbilling/PayInvoiceModal.vue'

import SelectPlanModal from '@/components/superbilling/SelectPlanModal.vue'

import BillingEmailsPanel from '@/components/superbilling/BillingEmailsPanel.vue'
import BillingCompanyDetailsPanel from '@/components/superbilling/BillingCompanyDetailsPanel.vue'

import DangerConfirmModal from '@/components/modals/DangerConfirmModal.vue'

import moment from 'moment'

export default {
  data () {
    return {
      invoices: [],

      selectedTab: 'plan',

      payingInvoice: null,
      selectingPlan: false,

      extendedAutoPayment: false,

      busy: false,

      confirmingAutoPaymentDisable: false
    }
  },
  methods: {
    showDisableAutopaymentModal () {
      this.confirmingAutoPaymentDisable = true
    },
    disableAutopayment () {
      SuperbillingAPI.disableAutoPayment(this.cafe._id)
        .then(data => {
          this.$store.commit('patchCafeGeneralData', {
            billing: {
              ...this.cafe.billing,
              auto_payment_enabled: false
            }
          })

          this.confirmingAutoPaymentDisable = false
        })
        .catch(err => {
          this.$store.commit('ui/addToast', {
            variant: 'error',
            content: 'superbilling_error'
          })
        })
    },
    fetchInvoices () {
      SuperbillingAPI.getInvoices(this.cafe._id).then(data => {
        this.invoices = data.invoices
      })
    },
    formatServiceName (name) {
      return (
        {
          inside: 'Inside',
          delivery: 'Delivery',
          takeaway: 'Takeaway',
          preorder: 'Preorder',
          preview: 'Preview'
        }[name] || name
      )
    },
    onPlanChange (params) {
      this.busy = true

      SuperbillingAPI.changePlan({
        ...params,
        cafe_id: this.cafe._id
      })
        .then(data => {
          this.busy = false
          this.$store.commit('setCafe', data.cafe)
          this.payingInvoice = data.invoice

          this.fetchInvoices()
        })
        .catch(err => {
          this.$store.commit('ui/addToast', {
            variant: 'error',
            content: 'superbilling_error'
          })
          this.busy = false
        })
    },
    capitalize (str) {
      if (!str) return str
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    getPaymentAttemptDate (attempt) {
      return moment(this.cafe.billing.paid_until).add(attempt, 'days').format('DD.MM.YYYY')
    }
  },
  computed: {
    cafe () {
      return this.$store.state.cafe
    },
    showUah () {
      return this.$store.state.worldwide.region_code === 'UA'
    },
    isBillingActive () {
      return this.cafe.billing.total_sum > 0
    },
    freePeriodDaysLeft () {
      if (!this.cafe.billing.trial_until) return 0

      const now = moment()
      const then = moment(this.cafe.billing.trial_until)

      if (now.isAfter(then)) return 0

      return Math.round(then.diff(now, 'days', true))
    },
    validTillDate () {
      return moment(this.cafe.billing.paid_until || this.cafe.billing.trial_until)
        .locale(this.$i18n.locale)
        .format('DD MMMM YYYY')
    },
    pricingPlanName () {
      return this.capitalize(this.cafe.billing.selected_plan)
    },
    calendarDayNumber () {
      if (!this.cafe.billing.paid_until) return 1

      return moment(this.cafe.billing.paid_until).date()
    },
    nextPaymentDate () {
      return moment(this.cafe.billing.paid_until)
        .add(this.cafe.billing.auto_payment_attempts || 0, 'days')
        .toDate()
    }
  },
  mounted () {
    this.fetchInvoices()

    if (this.$route.query.changePlan) {
      this.selectingPlan = true
      this.$router.replace('/superbilling')
    }
  },
  components: {
    PayInvoiceModal,
    SelectPlanModal,
    BillingEmailsPanel,
    BillingCompanyDetailsPanel,
    DangerConfirmModal
  }
}
</script>

<style>
.superbilling-global-container {
  padding: 16px 32px 32px 32px !important;
  display: flex;
  flex-direction: column;
  row-gap: 32px;
}
</style>

<style scoped>
.validity-block {
  width: 100%;
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  padding: 24px;
  box-sizing: border-box;
  border-radius: 12px;
  background: var(--qrw-background-base-primary);
  box-shadow: var(--qrw-shadow-card-light);
}

.validity-column {
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  width: 100%;
}

.no-plan-column {
  display: flex;
  flex-direction: row;
  column-gap: 12px;
  width: 100%;

  justify-content: space-between;
  align-items: center;
}

.billing-divider {
  min-width: 1px;
  width: 1px;
  height: 100%;
  background: var(--qrw-background-base-tertiary);
}

.change-plan {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.billing-table {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding-bottom: 2px;
  overflow-x: auto;
}

.billing-table-header {
  display: flex;
  flex-direction: row;
  padding: 0 12px;
  color: var(--qrw-content-base-tertiary);
  box-sizing: border-box;
  justify-content: space-between;
}

.bt-col-services {
  width: 330px;
  min-width: 330px;
}

.bt-col-short {
  width: 64px;
  min-width: 64px;
}

.bt-col-medium {
  width: 153px;
  min-width: 153px;
}

.bt-col-method {
  width: 191px;
  min-width: 191px;
}

.bt-col-sum {
  width: 108px;
  min-width: 108px;
}

.bt-col-status {
  width: 98px;
  min-width: 98px;
  white-space: nowrap;
}

.bt-col-actions {
  width: 178px;
  min-width: 178px;

  display: flex;
  justify-content: flex-end;
}

.billing-table-rows {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  padding: 2px;
  box-sizing: border-box;
}

.billing-table-item {
  display: flex;
  padding: 0 12px;
  background: var(--qrw-background-base-primary);
  box-shadow: var(--qrw-shadow-card-light);
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
  justify-content: space-between;
  min-width: fit-content;
}

.table-item-col {
  display: flex;
  padding: 22px 12px;
  align-items: center;
  box-sizing: border-box;
}

.table-item-col-plan {
  padding: 12px;
  row-gap: 4px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  box-sizing: border-box;
}

.header-column {
  padding: 0 12px;
  box-sizing: border-box;
}

.table-item-col.bt-col-actions {
  padding: 16px 12px;
  flex-direction: row;
  column-gap: 12px;
}

.bt-col-actions button {
  width: fit-content;
  min-width: fit-content;
}

.auto-payment-container {
  display: flex;
  flex-direction: column;
}

.auto-payment {
  width: 100%;
  background: var(--qrw-background-base-primary);
  box-shadow: var(--qrw-shadow-card-light);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: row;
  column-gap: 24px;
  box-sizing: border-box;
}

.auto-payment-status {
  align-items: center;
  width: 100%;
}

.auto-payment-container {
  background: var(--qrw-background-base-tertiary);
  border-radius: 12px;
}

.auto-payment-card,
.auto-payment-attempts {
  width: 100%;
  display: flex;
  flex-direction: row;
  column-gap: 16px;
  align-items: center;
}

.auto-payment-details {
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  row-gap: 24px;
  box-sizing: border-box;
}

.auto-payment-attempts {
  width: 100%;
  display: flex;
  column-gap: 40px;
  flex-direction: row;
}

.auto-payment-attempts {
  align-items: center;
}

.auto-payment-hint {
  text-align: center;
}
</style>